import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import { useApiKey } from '../contexts/ApiKeyContext';
import { 
  Save, 
  RefreshCw, 
  AlertCircle, 
  CheckCircle, 
  Loader2, 
  ChevronDown,
  Clock,
  User,
  HelpCircle
} from 'lucide-react';

const AgentPromptEditor = ({ onShowHelp }) => {
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  
  // Get API key from context
  const { apiKey, error: apiKeyError, isLoading: apiKeyLoading } = useApiKey();

  // Debug logging
  useEffect(() => {
    console.log('🔍 AgentPromptEditor - API Key Debug:');
    console.log('   API Key loading:', apiKeyLoading);
    console.log('   API Key available:', !!apiKey);
    console.log('   API Key error:', apiKeyError);
    console.log('   API Key (first 10 chars):', apiKey ? apiKey.substring(0, 10) + '...' : 'None');
  }, [apiKey, apiKeyError, apiKeyLoading]);

  // Fetch available agents for this user
  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(getApiUrl('/user/agents'), {
        withCredentials: true
      });
      
      if (response.data && response.data.agents) {
        setAgents(response.data.agents);
        console.log(`✅ Fetched ${response.data.count} agents for user`);
      } else {
        setAgents([]);
        console.log('✅ No agents found for user');
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Failed to fetch agents');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch the current system prompt for the selected agent
  const fetchAgentPrompt = async (agentId) => {
    if (!agentId || !apiKey) {
      console.log('🔍 fetchAgentPrompt - Skipping due to missing data:');
      console.log('   agentId:', agentId);
      console.log('   apiKey available:', !!apiKey);
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 fetchAgentPrompt - Making request with API key:', apiKey.substring(0, 10) + '...');
      
      const response = await axios.get(getApiUrl(`/agent/${agentId}/prompt`), {
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json'
        }
      });
      
      const data = response.data;
      setSystemPrompt(data.system_prompt || '');
      setOriginalPrompt(data.system_prompt || '');
      setLastUpdated(data.last_updated);
      setSelectedAgent(data);
      
      console.log('✅ Successfully fetched agent prompt');
      
    } catch (err) {
      console.error('Error fetching agent prompt:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.error || 'Failed to fetch agent prompt');
      setSystemPrompt('');
      setOriginalPrompt('');
      setLastUpdated(null);
      setSelectedAgent(null);
    } finally {
      setLoading(false);
    }
  };

  // Save the updated system prompt
  const savePrompt = async () => {
    if (!selectedAgentId || !apiKey) {
      console.log('🔍 savePrompt - Skipping due to missing data:');
      console.log('   selectedAgentId:', selectedAgentId);
      console.log('   apiKey available:', !!apiKey);
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      console.log('🔍 savePrompt - Making request with API key:', apiKey.substring(0, 10) + '...');
      
      const response = await axios.put(getApiUrl(`/agent/${selectedAgentId}/prompt`), {
        system_prompt: systemPrompt
      }, {
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json'
        }
      });
      
      const data = response.data;
      setOriginalPrompt(systemPrompt);
      setLastUpdated(data.last_updated);
      setSuccess('System prompt updated successfully!');
      
      console.log('✅ Successfully updated agent prompt');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Error updating agent prompt:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.error || 'Failed to update system prompt');
    } finally {
      setSaving(false);
    }
  };

  // Handle agent selection
  const handleAgentChange = (agentId) => {
    console.log('🔍 handleAgentChange - Selected agent:', agentId);
    setSelectedAgentId(agentId);
    setSystemPrompt('');
    setOriginalPrompt('');
    setLastUpdated(null);
    setSelectedAgent(null);
    setError('');
    setSuccess('');
    
    if (agentId) {
      fetchAgentPrompt(agentId);
    }
  };

  // Check if prompt has been modified
  const hasChanges = systemPrompt !== originalPrompt;

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  // Show loading state while API key is being fetched
  if (apiKeyLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-white mb-2">Agent Prompt Editor</h1>
            <p className="text-gray-400">
              Manage system prompts for your registered agents. Customize how each agent behaves and responds.
            </p>
          </div>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            <span className="ml-3 text-gray-300">Loading API key...</span>
          </div>
        </div>
      </div>
    );
  }

  // Show API key required message if no API key
  if (!apiKey) {
    console.log('🔍 AgentPromptEditor - Showing API key required message');
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-white mb-2">Agent Prompt Editor</h1>
            <p className="text-gray-400">
              Manage system prompts for your registered agents. Customize how each agent behaves and responds.
            </p>
          </div>
          <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6">
            <h3 className="font-semibold text-red-300 mb-2">API Key Required</h3>
            <p className="text-red-300">
              {apiKeyError || 'API key is required to access this feature. Please contact your admin.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Agent Prompt Editor</h1>
            <p className="text-gray-400">
              Manage system prompts for your registered agents. Customize how each agent behaves and responds.
            </p>
          </div>
          {onShowHelp && (
            <button
              onClick={onShowHelp}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-300"
            >
              <HelpCircle className="w-4 h-4" />
              <span>Help</span>
            </button>
          )}
        </div>

        {/* Agent Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Select Agent
          </label>
          <div className="relative">
            <select
              value={selectedAgentId}
              onChange={(e) => handleAgentChange(e.target.value)}
              className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              disabled={loading}
            >
              <option value="">Choose an agent...</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name} ({agent.agent_type} - {agent.llm})
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-900/20 border border-red-700/50 rounded-xl p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <span className="text-red-300">{error}</span>
            </div>
          </div>
        )}

        {/* Success Display */}
        {success && (
          <div className="mb-6 bg-green-900/20 border border-green-700/50 rounded-xl p-4">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <span className="text-green-300">{success}</span>
            </div>
          </div>
        )}

        {/* System Prompt Editor */}
        {selectedAgentId && (
          <div className="space-y-6">
            {/* Agent Info */}
            {selectedAgent && (
              <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{selectedAgent.agent_name}</h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-400 mt-1">
                      <span className="flex items-center space-x-1">
                        <User className="h-4 w-4" />
                        <span>Agent ID: {selectedAgent.agent_id}</span>
                      </span>
                      {lastUpdated && (
                        <span className="flex items-center space-x-1">
                          <Clock className="h-4 w-4" />
                          <span>Last updated: {formatDate(lastUpdated)}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Prompt Editor */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                System Prompt
              </label>
              <textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="Enter the system prompt for this agent..."
                className="w-full h-64 bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                disabled={loading || saving}
              />
              <p className="text-sm text-gray-400 mt-2">
                This prompt defines how the agent behaves and responds to user requests.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-4">
              <button
                onClick={savePrompt}
                disabled={!hasChanges || saving || loading}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                  hasChanges && !saving && !loading
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                }`}
              >
                {saving ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Save className="h-5 w-5" />
                )}
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>

              <button
                onClick={() => handleAgentChange(selectedAgentId)}
                disabled={loading}
                className="flex items-center space-x-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>

            {/* Changes Indicator */}
            {hasChanges && (
              <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-xl p-4">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-yellow-400" />
                  <span className="text-yellow-300">You have unsaved changes</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && !selectedAgentId && (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentPromptEditor; 